import { useCallback, useState } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import { QUERY_STATE } from '@tmapy/beetlejuice-utils';
import { SvgCross, SvgDownload, TertiaryBtn, TertiaryLink } from '@tmapy/style-guide';

import type { NestedCheckResponse } from '../../openapi/types';
import { docChecksInterruptPartialUpdate } from '../../openapi/operations/docChecksInterruptPartialUpdate';

import { useDownloadLink } from '../../hooks';
import { CHECK_STATE } from '../constants';
import { CheckBadge } from './CheckBadge';
import { docEditMessages as msg } from './docEditMessages';

type CheckRecordProps = NestedCheckResponse & {
  docId: number | undefined;
  onClick: (id: number) => void;
  onRefetch: () => void;
};

export const CheckRecord = (props: CheckRecordProps) => {
  const formatMessage = useMessage();

  const downloadUrl = props.url.replace('doc_checks', `docs/${props.docId}/checks`);
  const downloadCheckLink = useDownloadLink(downloadUrl, undefined, 'download_check');

  const [interruptQueryState, setInterrupQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const downloadIcon = {
    element: <SvgDownload />,
  };

  const interruptIcon = {
    element: <SvgCross />,
  };

  const handleClick = useCallback(() => {
    props.onClick(props.id);
  }, [props]);

  const handleInterruptBtnClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        setInterrupQueryState(QUERY_STATE.WAITING);
        const response = await docChecksInterruptPartialUpdate({
          query: { id: props.id },
        });
        if (response.ok) {
          setInterrupQueryState(QUERY_STATE.SUCCESS);
        } else {
          setInterrupQueryState(QUERY_STATE.FAIL);
        }
      } catch (e) {
        setInterrupQueryState(QUERY_STATE.FAIL);
      }
      props.onRefetch();
    },
    [props],
  );

  const isWaiting = interruptQueryState === QUERY_STATE.WAITING;
  return (
    <tr onClick={handleClick}>
      <td>
        <CheckBadge state={props.state} />
      </td>
      <td className='sg-u-c-body-darker'>
        <pre>{props.log}</pre>
      </td>
      <td>
        {props.checker?.name} {props.checker?.surname}
      </td>
      <td className='sg-a-ta-r'>
        <FormattedDate value={new Date(props.created)} />{' '}
        <FormattedTime
          value={new Date(props.created)}
          hour='numeric'
          minute='numeric'
          second='numeric'
        />
      </td>
      <td className='sg-a-ta-r'>
        {props.finished && (
          <>
            <FormattedDate value={new Date(props.finished)} />{' '}
            <FormattedTime
              value={new Date(props.finished)}
              hour='numeric'
              minute='numeric'
              second='numeric'
            />
          </>
        )}
      </td>
      <td className='sg-a-w-0' onClick={downloadCheckLink.onClick}>
        {(props.state === CHECK_STATE.FAILED || props.state === CHECK_STATE.PASSED) && (
          <TertiaryLink
            {...downloadCheckLink}
            icon={downloadIcon}
            tooltip={formatMessage(msg.download)}
          />
        )}
      </td>
      <td className='sg-a-w-0'>
        {(props.state === CHECK_STATE.CHECKING ||
          props.state === CHECK_STATE.WAITING_FOR_CHECKING) && (
          <TertiaryBtn
            onClick={handleInterruptBtnClick}
            icon={interruptIcon}
            isDanger
            queryState={isWaiting ? 'waiting' : undefined}
            tooltip={formatMessage(msg.checkInterruptBtn)}
          />
        )}
      </td>
    </tr>
  );
};
